import React, { memo, useMemo } from 'react';
import {
  useLocation,
  match,
  Link,
  useRouteMatch,
  matchPath
} from 'react-router-dom';
import { List, ListItem, ListItemText, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import TIRouteProps from 'modules/common/routes/TIRouteProps';
import { useDefaultRoutes } from './useDefaultRoutes';

type PageStyleProps = {
  selected: boolean;
};

const useListStyles = makeStyles((theme) => ({
  root: {
    marginLeft: theme.spacing(-0.5)
  }
}));

const useListItemStyles = makeStyles((theme) => ({
  root: {
    '&.css-cveggr-MuiListItemIcon-root': {
      color: undefined
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.text.primary
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.text.primary
    },
    '&.Mui-selected,.Mui-selected:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.text.primary
    },
    color: theme.palette.grey[400]
  }
}));

const useStyles = makeStyles((theme) => ({
  text: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.h6.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    color: ({ selected }: PageStyleProps) =>
      selected ? 'inherit' : theme.palette.grey[500],
    marginLeft: theme.spacing(1),
    ...theme.typography.body1
  },
  icon: {
    color: ({ selected }: PageStyleProps) =>
      selected ? theme.palette.text.primary : 'undefined',
    '&:hover': {
      background: 'none'
    }
  }
}));

export const buildLinkPath = (
  pathMatch: match,
  dynamicPath?: string
): string | undefined => {
  const pathEndsWithSlash = pathMatch.path.endsWith('/');
  const urlEndsWithSlash = pathMatch.url.endsWith('/');

  return dynamicPath?.replace(
    pathMatch.path,
    `${pathMatch.url}${pathEndsWithSlash && !urlEndsWithSlash ? '/' : ''}`
  );
};

type SidePanelOptionProps = {
  route: TIRouteProps;
  selected: boolean;
  path?: string;
};

export const SidePanelOption: React.FC<SidePanelOptionProps> = ({
  route,
  selected,
  path
}: SidePanelOptionProps) => {
  const routeMatch = useRouteMatch();
  const listItemClasses = useListItemStyles();
  const classes = useStyles({ selected });
  const primaryTypographyProps = useMemo(() => ({ className: classes.text }), [
    classes.text
  ]);

  return (
    <ListItem
      classes={listItemClasses}
      component={Link}
      to={route.link ? route.link : buildLinkPath(routeMatch, path) ?? '/'}
      selected={selected}
      button
      dense={false}
      data-testid={path}
    >
      <IconButton className={classes.icon} disableRipple>
        {route.icon}
      </IconButton>
      <ListItemText
        primary={route.title}
        primaryTypographyProps={primaryTypographyProps}
      />
    </ListItem>
  );
};

export const SidePanelOptions: React.FC = memo(() => {
  const location = useLocation();
  const routes = useDefaultRoutes();
  const classes = useListStyles();
  return (
    <List disablePadding classes={classes}>
      {Object.values(routes).map((route, index) => {
        const path = Array.isArray(route.path) ? route.path[0] : route.path;
        const selected = !!matchPath(location.pathname, route);

        return (
          <SidePanelOption
            route={route}
            path={path}
            selected={selected}
            key={index}
          />
        );
      })}
    </List>
  );
});

SidePanelOptions.displayName = 'SidePanelOptions';
